import { getToken as getAppCheckTokenSdk, type AppCheck } from "firebase/app-check";
import { useCallback, useMemo } from "react";
import { useAuth } from "../auth/context";
import { getFirebaseClient } from "../auth/firebase";
import { createControlApiClient, type ControlApiClient } from "./api";
import { createBoundedAppCheckTokenProvider } from "./appCheckToken";
import { getWebEnv, type WebEnv } from "./env";

const appCheckTokenProviders = new WeakMap<AppCheck, () => Promise<string | null>>();

export function getSharedAppCheckToken(env: WebEnv): Promise<string | null> {
  const { appCheck } = getFirebaseClient(env);
  if (!appCheck) return Promise.resolve(null);

  let provider = appCheckTokenProviders.get(appCheck);
  if (!provider) {
    provider = createBoundedAppCheckTokenProvider(async () => {
      const { token } = await getAppCheckTokenSdk(appCheck);
      return token;
    });
    appCheckTokenProviders.set(appCheck, provider);
  }
  return provider();
}

/** Binds a validated WebEnv. The app only mounts when one is available. */
export function getRequiredWebEnv(): WebEnv {
  const env = getWebEnv();
  if (!env) {
    throw new Error("WebEnv is not configured — copy apps/web/.env.example to apps/web/.env");
  }
  return env;
}

/** Control API client wired to the current auth session and WebEnv. */
export function useControlApiClient(): ControlApiClient {
  const { getIdToken, user, session, isCurrentSession } = useAuth();
  const ownerUid = user?.uid;
  const env = getRequiredWebEnv();

  const getAppCheckToken = useCallback(() => getSharedAppCheckToken(env), [env]);

  const getAccessToken = useCallback(() => getIdToken(), [getIdToken]);
  const assertSession = useCallback(() => {
    if (session && !isCurrentSession(session)) {
      throw new Error("The signed-in account changed. The request was cancelled.");
    }
    if (ownerUid && !session) {
      throw new Error("The signed-in session is not ready.");
    }
  }, [isCurrentSession, ownerUid, session]);

  return useMemo(
    () =>
      createControlApiClient(
        env,
        ownerUid ? getAccessToken : () => Promise.resolve(null),
        getAppCheckToken,
        assertSession
      ),
    [env, getAccessToken, getAppCheckToken, ownerUid, assertSession]
  );
}
